import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https.ts";
import { StoreModule } from "@/constants/store.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";

export interface UserLabel {
  id: string;
  name: string;
  color: string;
  usersCount: number;
}

export interface UserLabelsState {
  loading: boolean;
  labels: UserLabel[];
}

export const useUserLabelsStore = defineStore(StoreModule.USER_LABELS, {
  state: (): UserLabelsState => ({
    loading: false,
    labels: [],
  }),
  actions: {
    async getUserLabels(): Promise<void> {
      const { search } = useFiltersStore().filters[FilterContext.USERS_LABELS];
      this.loading = true;
      try {
        const request = {
          ...api.userLabels(),
          method: "GET",
          params: {
            search,
          },
        };
        const { data } = await axiosInstance.request<UserLabel[]>(request);
        this.labels = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async searchUserLabels(search: string = ""): Promise<UserLabel[]> {
      try {
        const request = {
          ...api.searchUserLabels(),
          method: "GET",
          params: {
            search,
          },
        };
        const { data } = await axiosInstance.request<UserLabel[]>(request);
        return data;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    async createUserLabel(data: Pick<UserLabel, "name" | "color">): Promise<void> {
      try {
        const request = {
          ...api.userLabels(),
          method: "POST",
          data: {
            name: data.name.trim(),
            color: data.color,
          },
        };
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.users.labelCreated", { ...data })
        );
        await this.getUserLabels();
      } catch (error) {
        console.error(error);
      }
    },
    async editUserLabel(data: UserLabel): Promise<void> {
      try {
        const request = {
          ...api.userLabel(data.id),
          method: "PUT",
          data: {
            ...data,
            name: data.name.trim(),
          },
        };
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.users.labelUpdated", { ...data })
        );
        await this.getUserLabels();
      } catch (error) {
        console.error(error);
      }
    },
    async deleteUserLabel(data: UserLabel): Promise<void> {
      try {
        const request = {
          ...api.userLabel(data.id),
          method: "DELETE",
        };
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.users.labelDeleted", { ...data })
        );
        await this.getUserLabels();
      } catch (error) {
        console.error(error);
      }
    },
  },
});
